import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Box, Fade, Button } from '@mui/material';

const MINUTE_HEIGHT_PX = 2.2;          // vertical scale: how tall one minute of the day is
const GRIDLINE_INTERVAL_MINUTES = 30;
const TICK_MS = 30000;                 // how often "now" advances
const NOW_OFFSET_PX = 26;              // breathing room above the now-line so its label isn't clipped
const CANVAS_INSET_PX = 26;            // leading canvas space so the topmost label has room to draw
const IDLE_SNAP_BACK_MS = 5 * 60000;   // after this long without interaction, scroll back to now
// How far from a programmatic scroll's target still counts as "our scroll, still settling".
// Generous enough to cover in-flight smooth-scroll frames, tight enough that a real drag
// (tens of pixels at minimum) is never mistaken for one.
const AUTO_SCROLL_TOLERANCE_PX = 4;
const DAY_START_HOUR = 6;              // canvas spans 6am..
const DAY_END_HOUR = 22;               //   ..10pm, so the whole working day is scrollable

function minutesBetween(from, to) {
  return (to.getTime() - from.getTime()) / 60000;
}

function formatTime(date) {
  return date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
}

function formatDuration(minutes) {
  const total = Math.round(minutes);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr${hours > 1 ? 's' : ''}`;
  return `${hours} hr${hours > 1 ? 's' : ''} ${mins} min`;
}

// Ticks on an interval so the now-line keeps advancing between data refreshes.
function useCurrentTime(intervalMs = TICK_MS) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return now;
}

function MeetingAgendaDisplay({ meetings }) {
  const now = useCurrentTime();
  const scrollRef = useRef(null);
  // Where an in-flight programmatic scroll is heading, or null when we aren't steering.
  // Comparing against this - rather than using a fixed timeout - is what makes the guard
  // survive iOS momentum scrolling, which keeps firing scroll events long after the flick.
  const autoScrollTargetRef = useRef(null);
  // True while a programmatic scroll is still animating toward that target.
  const isAutoScrollInFlightRef = useRef(false);
  // True from touchstart until touchend, so a finger on the glass always counts as the user.
  const isTouchingRef = useRef(false);
  const [isUserScrolled, setIsUserScrolled] = useState(false);
  // Bumped on every scroll gesture; the idle timer keys off it so each new gesture restarts
  // the 5 minutes rather than counting from the first one.
  const [lastActivity, setLastActivity] = useState(0);

  // The canvas covers the whole day so any time is reachable by scrolling; `now` is a
  // position within it rather than the top edge.
  const dayStart = useMemo(() => {
    const start = new Date(now);
    start.setHours(DAY_START_HOUR, 0, 0, 0);
    return start;
  }, [now.toDateString()]);

  const dayEnd = useMemo(() => {
    const end = new Date(now);
    end.setHours(DAY_END_HOUR, 0, 0, 0);
    return end;
  }, [now.toDateString()]);

  // Position of a time on the canvas. Everything is shifted down by CANVAS_INSET_PX so the
  // topmost label has canvas above it to draw on instead of being cut off.
  const topForTime = useCallback(
    (time) => CANVAS_INSET_PX + minutesBetween(dayStart, time) * MINUTE_HEIGHT_PX,
    [dayStart.getTime()],
  );

  // Scrolls so the now-line sits NOW_OFFSET_PX below the container's top edge, which is what
  // leaves visible space for the time label drawn above the line.
  const scrollTopForNow = useCallback(
    () => Math.max(0, topForTime(new Date()) - NOW_OFFSET_PX),
    [topForTime],
  );

  const scrollToNow = useCallback((behavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el) return;
    const target = scrollTopForNow();
    // Remember where we're steering so handleScroll can tell our own scrolling - including
    // the many events a smooth/momentum scroll emits - apart from a real touch.
    autoScrollTargetRef.current = target;
    isAutoScrollInFlightRef.current = behavior === 'smooth';
    if (behavior === 'smooth' && typeof el.scrollTo === 'function') {
      try {
        el.scrollTo({ top: target, behavior: 'smooth' });
      } catch {
        // Older Safari rejects the options object; fall back to an instant jump.
        el.scrollTop = target;
        isAutoScrollInFlightRef.current = false;
      }
    } else {
      el.scrollTop = target;
    }
    setIsUserScrolled(false);
  }, [scrollTopForNow]);

  // Start at now, without animation.
  useEffect(() => {
    scrollToNow('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep following the clock while the user hasn't taken over. Only corrects once the view has
  // drifted a few pixels, so the 30s tick doesn't fire a pointless sub-pixel smooth scroll.
  useEffect(() => {
    if (isUserScrolled) return;
    const el = scrollRef.current;
    if (!el) return;
    if (Math.abs(el.scrollTop - scrollTopForNow()) > 2) scrollToNow('smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now.getTime(), isUserScrolled]);

  // After IDLE_SNAP_BACK_MS with no interaction, hand the view back to the clock. Keying on
  // lastActivity means each fresh gesture cancels the pending timer and starts a new 5 minutes.
  useEffect(() => {
    if (!isUserScrolled) return undefined;
    const id = setTimeout(() => scrollToNow('smooth'), IDLE_SNAP_BACK_MS);
    return () => clearTimeout(id);
  }, [isUserScrolled, lastActivity, scrollToNow]);

  // Marks the user as in control and restarts the idle countdown.
  const markUserActivity = useCallback(() => {
    autoScrollTargetRef.current = null;
    isAutoScrollInFlightRef.current = false;
    setIsUserScrolled(true);
    setLastActivity((n) => n + 1);
  }, []);

  // Decides whether a scroll event came from the user or from our own scrollToNow. A finger on
  // the glass is always the user. Otherwise we're still steering as long as the position is
  // converging on our target - iOS fires scroll events throughout a smooth or momentum scroll,
  // so a time-based guard would misread those as input.
  const handleScroll = () => {
    if (isTouchingRef.current) {
      markUserActivity();
      return;
    }
    // A programmatic scroll owns every event until it arrives at its target. Arrival does not
    // clear the target: WebKit emits further scroll events once layout settles, and treating
    // those as input is what showed "Back to now" on a freshly loaded page. Only real user
    // input (touch, wheel, or a position that isn't ours) clears it.
    const el = scrollRef.current;
    const target = autoScrollTargetRef.current;
    if (target !== null && el) {
      const arrived = Math.abs(el.scrollTop - target) <= AUTO_SCROLL_TOLERANCE_PX;
      if (arrived) {
        isAutoScrollInFlightRef.current = false;
        return;
      }
      // Still animating toward the target - also ours.
      if (isAutoScrollInFlightRef.current) return;
    }
    markUserActivity();
  };

  const handleTouchStart = () => {
    isTouchingRef.current = true;
    markUserActivity();
  };

  // Momentum continues after the finger lifts; keep ownership with the user and let the idle
  // timer (restarted here) hand the view back once everything has settled.
  const handleTouchEnd = () => {
    isTouchingRef.current = false;
    markUserActivity();
  };

  // Meetings across the whole day, with real minute-precision times retained for display.
  const blocks = useMemo(() => {
    return (meetings ?? [])
      .map((meeting) => ({
        subject: meeting.subject,
        start: new Date(`${meeting.start.dateTime}Z`),
        end: new Date(`${meeting.end.dateTime}Z`),
      }))
      .filter((block) => block.end > dayStart && block.start < dayEnd)
      .sort((a, b) => a.start - b.start)
      .map((block) => {
        const visibleStart = block.start < dayStart ? dayStart : block.start;
        const visibleEnd = block.end > dayEnd ? dayEnd : block.end;
        const heightPx = Math.max(minutesBetween(visibleStart, visibleEnd) * MINUTE_HEIGHT_PX, 22);
        const isInProgress = block.start <= now && block.end > now;
        // How far into the block the now-line falls; text is pushed below it so the line
        // doesn't strike through the subject. Capped to keep at least one line visible.
        const elapsedPx = isInProgress ? minutesBetween(visibleStart, now) * MINUTE_HEIGHT_PX : 0;
        return {
          ...block,
          topPx: topForTime(visibleStart),
          heightPx,
          isInProgress,
          isPast: block.end <= now,
          textOffsetPx: Math.max(0, Math.min(elapsedPx + 3, heightPx - 22)),
        };
      })
      .map((block, index, all) => ({
        ...block,
        // Back-to-back with the previous meeting: needs a hard division, not a gap.
        abutsPrevious: index > 0 && all[index - 1].end.getTime() === block.start.getTime(),
      }));
  }, [meetings, dayStart.getTime(), dayEnd.getTime(), now.getTime(), topForTime]);

  const upcomingCount = blocks.filter((block) => !block.isPast).length;

  // Gridlines at clean half-hour marks across the day.
  const gridlines = useMemo(() => {
    const lines = [];
    for (let time = new Date(dayStart); time <= dayEnd; time = new Date(time.getTime() + GRIDLINE_INTERVAL_MINUTES * 60000)) {
      lines.push({ time, topPx: topForTime(time) });
    }
    return lines;
  }, [dayStart.getTime(), dayEnd.getTime(), topForTime]);

  const canvasHeightPx = minutesBetween(dayStart, dayEnd) * MINUTE_HEIGHT_PX;
  const gutterWidthPx = 62;

  return (
    <Paper sx={{ minHeight: { md: '90vh', sm: 'auto' }, p: 2, backgroundColor: 'rgba(246, 247, 248, 0.5)' }} square={false}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 40 }}>
        <Typography variant="h6">Upcoming Meetings</Typography>
        {/* Only offered while the user has scrolled away - otherwise the view is already at now. */}
        <Fade in={isUserScrolled}>
          <Button
            variant="outlined"
            onClick={() => scrollToNow('smooth')}
            // Sized for a finger on a wall-mounted iPad, not a mouse pointer.
            sx={{ fontWeight: 'bold', minHeight: 44, px: 2, whiteSpace: 'nowrap' }}
          >
            Back to now
          </Button>
        </Fade>
      </Box>
      {upcomingCount === 0 && (
        <Typography sx={{ color: 'text.secondary' }}>
          Nothing booked for the rest of the day.
        </Typography>
      )}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        onWheel={markUserActivity}
        onTouchStart={handleTouchStart}
        onTouchMove={markUserActivity}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        sx={{
          position: 'relative',
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: '78vh',
          mt: 1,
          // Momentum scrolling on iOS, which is off by default for overflow containers.
          WebkitOverflowScrolling: 'touch',
          // Keeps a vertical flick from also triggering Safari's back-swipe / page bounce.
          overscrollBehaviorY: 'contain',
        }}
      >
        {/* Extra height top and bottom so the first and last time labels - which are drawn
            above their gridline - always have canvas to sit on rather than being clipped.
            CANVAS_INSET_PX of leading space is added to every position via topForTime. */}
        <Box sx={{ position: 'relative', height: `${canvasHeightPx + CANVAS_INSET_PX * 2}px` }}>
          {/* Half-hour gridlines with their labels in the left gutter. */}
          {gridlines.map(({ time, topPx }) => (
            <Box key={time.getTime()} sx={{ position: 'absolute', top: `${topPx}px`, left: 0, right: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: '-0.7em',
                  left: 0,
                  width: `${gutterWidthPx - 10}px`,
                  textAlign: 'right',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatTime(time)}
              </Typography>
              <Box sx={{ position: 'absolute', left: `${gutterWidthPx}px`, right: 0, borderTop: '1px solid', borderColor: 'rgba(0,0,0,0.08)' }} />
            </Box>
          ))}

          {/* Meeting blocks, positioned and sized to real minute-precision times. */}
          {blocks.map((block) => (
            <Box
              key={`${block.start.getTime()}-${block.subject}`}
              sx={{
                position: 'absolute',
                top: `${block.topPx}px`,
                left: `${gutterWidthPx + 8}px`,
                right: 4,
                height: `${block.heightPx}px`,
                overflow: 'hidden',
                zIndex: 2,
                boxSizing: 'border-box',
                borderRadius: 1,
                px: 1,
                py: 0.25,
                opacity: block.isPast ? 0.45 : 1,
                backgroundColor: block.isInProgress ? 'rgba(211, 47, 47, 0.14)' : 'rgba(25, 118, 210, 0.12)',
                borderLeft: '4px solid',
                borderColor: block.isInProgress ? 'error.main' : 'primary.main',
                // Back-to-back meetings get a hard divider so the boundary is unmistakable.
                borderTop: block.abutsPrevious ? '2px solid rgba(0,0,0,0.45)' : 'none',
              }}
            >
              {/* For an in-progress meeting the now-line crosses the block, so shift the text
                  into the remaining time below it rather than letting the line strike through. */}
              <Box sx={{ pt: block.textOffsetPx ? `${block.textOffsetPx}px` : 0 }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 'bold', lineHeight: 1.25, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {block.subject || '(No subject)'}
              </Typography>
              {block.heightPx - (block.textOffsetPx || 0) >= 34 && (
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.2 }}>
                  {formatTime(block.start)} - {formatTime(block.end)}
                  {' · '}
                  {formatDuration(minutesBetween(block.start, block.end))}
                  {block.isInProgress ? ' · in progress' : ''}
                </Typography>
              )}
              </Box>
            </Box>
          ))}

          {/* The "now" marker, drawn last so it sits above the blocks it crosses. */}
          <Box sx={{ position: 'absolute', top: `${topForTime(now)}px`, left: 0, right: 0, zIndex: 4 }}>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '-0.7em',
                left: 0,
                width: `${gutterWidthPx - 10}px`,
                textAlign: 'right',
                fontWeight: 'bold',
                color: 'error.main',
                whiteSpace: 'nowrap',
              }}
            >
              {formatTime(now)}
            </Typography>
            {/* Semi-transparent so it reads as an overlay when it crosses an in-progress
                meeting, rather than striking through that meeting's text. */}
            <Box sx={{ position: 'absolute', left: `${gutterWidthPx}px`, right: 0, borderTop: '2px solid', borderColor: 'error.main', opacity: 0.55 }} />
            <Box
              sx={{
                position: 'absolute',
                left: `${gutterWidthPx - 4}px`,
                top: '-4px',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'error.main',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

MeetingAgendaDisplay.propTypes = {
  meetings: PropTypes.arrayOf(PropTypes.shape({
    subject: PropTypes.string,
    start: PropTypes.shape({ dateTime: PropTypes.string }),
    end: PropTypes.shape({ dateTime: PropTypes.string }),
  })),
};

export default MeetingAgendaDisplay;
